import { JobSummary } from 'redux/selectors/internal/DataRuns'

/**
 * Suffixes that mark a job as a customer's ontology/data-quality step
 * rather than a source-system connector job feeding into it. Mirrors
 * `NON_SOURCE_SUFFIXES` / `isSourceJob` in
 * `homebase_apis/my_homepal_api/services/Internal/DataRuns.ts` - kept as a
 * frontend-side literal since the wire contract carries no dependency
 * graph (see the design handoff's open question on the real dependency
 * source).
 */
const NON_SOURCE_SUFFIXES = [
  '_standard_ontology',
  '_custom_ontology',
  '_data_quality'
]

function isLikelySourceJob(jobName: string): boolean {
  return !NON_SOURCE_SUFFIXES.some((suffix) => jobName.endsWith(suffix))
}

/**
 * "Beroenden" for the detail panel: when the selected job is a
 * `{technicalName}_standard_ontology` run, the jobs it depends on are every
 * other job in `jobs` for the same customer that isn't itself an
 * ontology/data-quality step. Returns `null` when the selected job isn't an
 * ontology job, or when `jobs` doesn't contain any such source jobs for its
 * customer (e.g. the all-customers scope, or a customer loaded with only
 * the ontology job) - the panel omits the section entirely rather than
 * showing an empty block.
 */
export function deriveDependencyJobs(
  job: JobSummary,
  jobs: JobSummary[]
): JobSummary[] | null {
  if (!job.job_name.endsWith('_standard_ontology') || !job.customer) {
    return null
  }

  const dependencies = jobs.filter(
    (candidate) =>
      candidate.job_name !== job.job_name &&
      candidate.customer?.id === job.customer?.id &&
      isLikelySourceJob(candidate.job_name)
  )

  return dependencies.length > 0 ? dependencies : null
}

/**
 * A dependency's dot is red when it finished after the ontology job it
 * feeds had already started - that's the order problem this section
 * exists to surface. A dependency that hasn't completed at all yet is
 * treated the same way, since it can only finish even later.
 */
export function isDependencyLate(
  dependency: JobSummary,
  job: JobSummary
): boolean {
  if (!dependency.completed_at) {
    return true
  }

  return (
    new Date(dependency.completed_at).getTime() >
    new Date(job.started_at).getTime()
  )
}
